import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationService,
  LoginResponse,
  resolveRedirectTarget,
  User,
  UserProvider,
} from '@siat-fe/auth';
import { SnackbarService } from '@siat-fe/shared';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { HttpResponseStatus } from '../enums';

/**
 * whether a token refresh is currently in flight.
 */
let isRefreshing = false;

/**
 * emits once per refresh attempt (the new token, or null on failure) so queued requests can wait for it.
 */
const refreshTokenSubject = new Subject<string | null>();

/**
 * checks the skip-error-handling header.
 * @returns {boolean} true if this request opted out of generic error handling.
 */
function shouldSkipErrorHandling(request: HttpRequest<unknown>): boolean {
  return request.headers.has('skip-error-handling');
}

/**
 * clones a request with the refreshed bearer token.
 * @returns {HttpRequest<unknown>} the cloned request.
 */
function cloneRequestWithToken(request: HttpRequest<unknown>, token: string) {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * shows a session-expired snackbar and redirects to /logout, preserving the current url.
 */
function handleRefreshTokenError(
  router: Router,
  snackbarService: SnackbarService,
): void {
  const currentUrl =
    resolveRedirectTarget(window.location.pathname + window.location.search) ??
    undefined;
  snackbarService.showDangerSnackbar(
    'La tua sessione è scaduta. Effettua nuovamente il login.',
  );
  router.navigate(['/logout'], {
    queryParams: { sessionExpired: true, redirectTo: currentUrl },
    replaceUrl: true,
  });
}

/**
 * shows a snackbar appropriate to the given http status code.
 */
function showErrorSnackbar(
  snackbarService: SnackbarService,
  status: number,
  message: string,
): void {
  const errorMessage = message || 'Si è verificato un errore. Riprova.';

  switch (status) {
    case HttpResponseStatus.FORBIDDEN:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.NOT_FOUND:
    case HttpResponseStatus.INTERNAL_SERVER_ERROR:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.BAD_REQUEST:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.RANGE_NOT_SATISFIABLE:
      snackbarService.showDangerSnackbar(
        'La tua sessione è scaduta. Effettua nuovamente il login.',
      );
      break;
    case HttpResponseStatus.METHOD_NOT_ALLOWED:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.UNAUTHORIZED:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.NOT_ACCEPTABLE:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.CONFLICT:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    case HttpResponseStatus.PRECONDITION_FAILED:
    case HttpResponseStatus.PRECONDITION_REQUIRED:
    case HttpResponseStatus.PRECONDITION_ERROR:
      break;
    case HttpResponseStatus.GENERAL_ERROR:
      snackbarService.showDangerSnackbar(errorMessage);
      break;
    default:
      snackbarService.showDangerSnackbar('Si è verificato un errore. Riprova.');
      break;
  }
}

/**
 * refreshes the token and retries the request, queueing behind an already in-flight refresh if needed.
 * @returns {Observable<HttpEvent<unknown>>} the retried request's http event stream, or a rethrown error.
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthenticationService,
  userProvider: UserProvider,
  router: Router,
  snackbarService: SnackbarService,
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;

    return authService.refreshToken().pipe(
      switchMap((loginResponse: LoginResponse) => {
        isRefreshing = false;
        userProvider.setCredentials(loginResponse);
        refreshTokenSubject.next(loginResponse.token);
        return authService.getUserInfo(loginResponse.user_id).pipe(
          switchMap((user: User) => {
            userProvider.setCurrentUser(user);
            userProvider.setIsAdmin(user?.administrator || false);
            return next(cloneRequestWithToken(request, loginResponse.token));
          }),
        );
      }),
      catchError((refreshError) => {
        isRefreshing = false;
        refreshTokenSubject.next(null);
        handleRefreshTokenError(router, snackbarService);
        return throwError(() => refreshError);
      }),
    );
  } else {
    return refreshTokenSubject.pipe(
      take(1),
      switchMap((token) => {
        if (!token) {
          handleRefreshTokenError(router, snackbarService);
          return throwError(
            () => new Error('Token refresh fallito, richiesta interrotta'),
          );
        }
        return next(cloneRequestWithToken(request, token));
      }),
    );
  }
}

/**
 * intercepts http errors, retrying via token refresh on 401/416 and showing a snackbar otherwise.
 * @returns {Observable<HttpEvent<unknown>>} the http event stream, or a rethrown/mapped error.
 */
export const errorHandlerInterceptor: HttpInterceptorFn = (request, next) => {
  const router = inject(Router);
  const snackbarService = inject(SnackbarService);
  const authService = inject(AuthenticationService);
  const userProvider = inject(UserProvider);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      if (shouldSkipErrorHandling(request)) {
        return throwError(() => error);
      }

      if (
        error.status === HttpResponseStatus.UNAUTHORIZED ||
        error.status === HttpResponseStatus.RANGE_NOT_SATISFIABLE
      ) {
        return handle401Error(
          request,
          next,
          authService,
          userProvider,
          router,
          snackbarService,
        );
      }

      showErrorSnackbar(
        snackbarService,
        error.status,
        error.error?.errorMessage,
      );
      return throwError(() => error);
    }),
  );
};
